import { webCoreTypes } from "@core/core-types.di";
import { FallbackError } from "@core/domain/errors/fallback.error";
import type { IotApiClient } from "@iot-platform/core";
import type {
    NotificationAlertDto,
    NotificationAlertsDto,
    NotificationAlertUpdateBody,
} from "@station/data/dto/notification-alert.dto";
import { NotificationAlertMapper } from "@station/data/mappers/notification-alert.mapper";
import type { NotificationAlertUpdateError } from "@station/domain/errors/alert/notification-alert-update.error";
import type {
    NotificationAlerts,
    NotificationAlertUpdate,
} from "@station/domain/models/notification-alert.model";
import { inject, injectable } from "inversify";
import type { ResultAsync } from "neverthrow";
import { ok } from "neverthrow";

@injectable()
export class NotificationAlertDatasource {
    static readonly NOTIFICATION_ALERT_PATH =
        "/stations/:stationId/notifications";

    constructor(
        @inject(webCoreTypes.infrastructure.IotApiClient)
        private readonly iotApiClient: IotApiClient,
        @inject(NotificationAlertMapper)
        private readonly notificationAlertMapper: NotificationAlertMapper,
    ) {}

    fetchAll(
        stationId: string,
    ): ResultAsync<NotificationAlerts, FallbackError> {
        return this.iotApiClient.http
            .get<NotificationAlertsDto>(
                NotificationAlertDatasource.NOTIFICATION_ALERT_PATH.replace(
                    ":stationId",
                    stationId,
                ),
            )
            .mapErr(() => new FallbackError())
            .map((response) =>
                this.notificationAlertMapper.mapList(response.data),
            );
    }

    update(
        updateNotificationAlert: NotificationAlertUpdate,
    ): ResultAsync<boolean, NotificationAlertUpdateError> {
        const notificationAlertUpdateBody: NotificationAlertUpdateBody = {
            read: updateNotificationAlert.read,
        };

        return this.iotApiClient.http
            .patch<NotificationAlertDto>(
                `notifications/${updateNotificationAlert.id}`,
                notificationAlertUpdateBody,
            )
            .mapErr(() => new FallbackError())
            .andThen(() => ok(true));
    }

    delete(
        notificationAlertId: string,
    ): ResultAsync<boolean, NotificationAlertUpdateError> {
        return this.iotApiClient.http
            .delete(`notifications/${notificationAlertId}`)
            .mapErr(() => new FallbackError())
            .andThen(() => ok(true));
    }
}
